import { ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Injectable } from '@angular/core';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { BehaviorSubject } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';


/********************************************************************************** */
/**
 * Node for to-do item
 */
export class TodoItemNode {
  children: TodoItemNode[];
  item: string;
}

/** Flat to-do item node with expandable and level information */
export class TodoItemFlatNode {
  item: string;
  level: number;
  expandable: boolean;
}

/**
 * The Json object for to-do list data.
 */
const TREE_DATA = {
  All: {
    
  },
};

//****************************************************************************** */
@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<TodoItemNode[]>([]);

  get data(): TodoItemNode[] {
    return this.dataChange.value;
  }

  constructor() {
  }

  initialize(treeData: any) {
    // Convierte el objeto a un árbol
    const data = this.buildFileTree(treeData, 0);
    // Notifica el cambio.
    this.dataChange.next(data);
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `TodoItemNode`.
   */
  buildFileTree(obj: { [key: string]: any }, level: number): TodoItemNode[] {
    const returnArr: TodoItemNode[] = [];
  
    const monthOrder = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
  
    for (const k in obj) {
      if (obj.hasOwnProperty(k)) {
        const node = new TodoItemNode();
        node.item = `${k}`;
        if (obj[k] != null) {
          if (typeof obj[k] === 'object') {
            node.children = this.buildFileTree(obj[k], level + 1);
            if (Array.isArray(obj[k]) && monthOrder.includes(obj[k][0])) {
              // Ordenar meses si el array contiene nombres de meses
              node.children.sort((a, b) => monthOrder.indexOf(a.item) - monthOrder.indexOf(b.item));
            } else {
              // Ordenar años de manera ascendente
              node.children.sort((a, b) => parseInt(a.item) - parseInt(b.item));
            }
          } else {
            node.item = `${obj[k]}`;
          }
        }
        returnArr.push(node);
      }
    }
  
    // Ordenar años de manera ascendente para el nivel raíz
    if (level === 0) {
      returnArr.sort((a, b) => parseInt(a.item) - parseInt(b.item));
    }
  
    return returnArr;
  }  
  
  
}

@Component({
  selector: 'app-checkbox-tree-month',
  templateUrl: './checkbox-tree-month.component.html',
  styleUrls: ['./checkbox-tree-month.component.css'],
  providers: [ChecklistDatabase],
  animations: [
    trigger('showHide', [
      state('show', style({
        opacity: 1,
        display: 'block'
      })),
      state('hide',   style({
        opacity: 0,
        display: 'none'
      })),
      transition('show => hide', animate('300ms ease-out')),
      transition('hide => show', animate('300ms ease-in'))
    ])
  ]
})
export class CheckboxTreeMonthComponent implements OnChanges {
  @Input() selectDatesTitle: any = 'Select date';
  @Input() datesArray: any;
  @Output() datesSelected = new EventEmitter<string[]>();
  @Input() disabled?: boolean = false;
  

  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TodoItemFlatNode, TodoItemNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<TodoItemNode, TodoItemFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: TodoItemFlatNode | null = null;

  /** The new item's name */
  newItemName = '';

  treeControl: FlatTreeControl<TodoItemFlatNode>;

  treeFlattener: MatTreeFlattener<TodoItemNode, TodoItemFlatNode>;

  dataSource: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<TodoItemFlatNode>(
    true /* multiple */
  );

  
  constructor(private _database: ChecklistDatabase, private elRef: ElementRef) {
    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this.getLevel,
      this.isExpandable,
      this.getChildren
    );
    this.treeControl = new FlatTreeControl<TodoItemFlatNode>(
      this.getLevel,
      this.isExpandable
    );
    this.dataSource = new MatTreeFlatDataSource(
      this.treeControl,
      this.treeFlattener
    );

    _database.dataChange.subscribe((data) => {
      this.dataSource.data = data;
    });

    this.checklistSelection.changed.subscribe(() => {
      this.updateSelectedItems();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['datesArray']) {
      this._database.initialize(this.datesArray);
    }
  }

  getLevel = (node: TodoItemFlatNode) => node.level;

  isExpandable = (node: TodoItemFlatNode) => node.expandable;

  getChildren = (node: TodoItemNode): TodoItemNode[] => node.children;

  hasChild = (_: number, _nodeData: TodoItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: TodoItemFlatNode) =>
    _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: TodoItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode =
      existingNode && existingNode.item === node.item
        ? existingNode
        : new TodoItemFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    flatNode.expandable = !!node.children?.length;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  };

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    return descAllSelected;
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some((child) =>
      this.checklistSelection.isSelected(child)
    );
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);

    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants)
      : this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach((child) => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: TodoItemFlatNode): void {
    let parent: TodoItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: TodoItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected =
      descendants.length > 0 &&
      descendants.every((child) => {
        return this.checklistSelection.isSelected(child);
      });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: TodoItemFlatNode): TodoItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  isYearNode(node: TodoItemFlatNode): boolean {
    return node.level === 1;
  }

  isMonthNode(node: TodoItemFlatNode): boolean {
    return node.level === 2;
  }

  isDayNode(node: TodoItemFlatNode): boolean {
    return node.level === 3;
  }

  getMonthIndex(monthName: string): number {
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    return months.indexOf(monthName);
  }

  getFormattedSelectedItems(): string[] {
    const selectedNodes = this.checklistSelection.selected;
    const selectedDates: string[] = [];
  
    for (const node of selectedNodes) {
      let parentNode = this.flatNodeMap.get(node);
      if (parentNode?.children) {
        // Skip non-leaf nodes
        continue;
      }
  
      let month = node.item;
      let yearNode = this.getParentNode(node);
      let year = yearNode ? yearNode.item : '';
  
      if (month && year) {
        let monthIndex = this.getMonthIndex(month) + 1; // Convert month name to month number
        let paddedMonthIndex = monthIndex.toString().padStart(2, '0');  // Add leading zero if needed
        selectedDates.push(`${paddedMonthIndex}/${year}`);
      }
    }
  
    return selectedDates;
  }
  

  
  

  public selectedItems: string[] = [];

  showSelectedItems(): void {
    this.selectedItems = this.getFormattedSelectedItems();
  }

  getSelectedItems(): TodoItemFlatNode[] {
    return this.checklistSelection.selected;
  }


  private updateSelectedItems(): void {
    const selectedItems = this.getFormattedSelectedItems();
    this.datesSelected.emit(selectedItems); // Emite el evento
  }

  showContainer = false;

  toggleContainer() {
    this.showContainer = !this.showContainer;
  }


  @HostListener('document:click', ['$event'])
  handleClick(event: Event) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.showContainer = false;
    }
  }

  @ViewChild('treeContainer', { static: true, read: ElementRef }) containerElement: ElementRef;

  onScroll(event: WheelEvent): void {
    event.stopPropagation();
  }
  
  
  
}
